import { useState } from "react";
import { Link } from "wouter";
import { useListSpaces } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { motion } from "framer-motion";
import { Box, Star, ExternalLink, Theater, Users, Music, Sofa } from "lucide-react";
import { useInfiniteItems } from "@/hooks/useInfiniteItems";
import avatarCity from "@assets/IMG_6897_1779819286654.jpeg";
import atlantaImg from "@assets/Atlanta1_1780074580456.png";
import miamiImg from "@assets/Miami1_1780074580452.png";

const SPATIAL_CITIES = [
  {
    name: "ISM | Dallas Digi-City",
    cityLabel: "Earth: US Cities · Dallas, TX",
    description: "The Illusionists Theatres · Spatial Environment · Live Events",
    url: "https://www.spatial.io/s/ISMProdCo-Dallas-Digi-City-673760fec6f66862305dca72?share=84904098136299549",
    image: null,
    accentBorder: "border-purple-500/30 hover:border-purple-400/70",
    accentText: "text-purple-400",
    accentBadge: "bg-purple-500/20 border-purple-500/40 text-purple-300",
  },
  {
    name: "ISM | Atlanta Digi-City",
    cityLabel: "Earth: US Cities · Atlanta, GA",
    description: "The Illusionists Theatres · Spatial Environment · Live Events",
    url: "https://www.spatial.io/s/ISMProdCo-Atlanta-Digi-City-6719521e50e4fad03021ebbe?share=1422014762304422329",
    image: atlantaImg,
    accentBorder: "border-green-500/30 hover:border-green-400/70",
    accentText: "text-green-400",
    accentBadge: "bg-green-500/20 border-green-500/40 text-green-300",
  },
  {
    name: "ISM | Miami Digi-City",
    cityLabel: "Earth: US Cities · Miami, FL",
    description: "The Illusionists Theatres · Spatial Environment · Live Events",
    url: "https://www.spatial.io/s/ISMProdCo-Miami-Digi-City-673755b7c6f66862305dca65?share=8280290324563973024",
    image: miamiImg,
    accentBorder: "border-cyan-500/30 hover:border-cyan-400/70",
    accentText: "text-cyan-400",
    accentBadge: "bg-cyan-500/20 border-cyan-500/40 text-cyan-300",
  },
];

const IMVU_ROOMS = [
  {
    id: "356174852-41",
    url: "https://www.imvu.com/next/chat/room-356174852-41/",
    name: "ISM Event Hall",
    description: "The main stage for live ISM performances, DJ sets, and hosted events. Capacity crowds, energy on max.",
    Icon: Music,
    accent: "orange",
    accentFrom: "from-orange-950",
    accentVia: "via-amber-950",
    accentBorder: "border-orange-500/30 hover:border-orange-400/60",
    accentText: "text-orange-400",
    accentBadge: "bg-orange-500/20 border-orange-500/40 text-orange-300",
    accentGlow: "bg-[radial-gradient(ellipse_at_bottom_left,_rgba(249,115,22,0.25)_0%,_transparent_65%)]",
  },
  {
    id: "381087765-13",
    url: "https://www.imvu.com/next/chat/room-381087765-13/",
    name: "ISM Performance Stage",
    description: "Purpose-built for live shows, avatar performances, and spotlight moments in the ISM Universe.",
    Icon: Theater,
    accent: "pink",
    accentFrom: "from-pink-950",
    accentVia: "via-rose-950",
    accentBorder: "border-pink-500/30 hover:border-pink-400/60",
    accentText: "text-pink-400",
    accentBadge: "bg-pink-500/20 border-pink-500/40 text-pink-300",
    accentGlow: "bg-[radial-gradient(ellipse_at_top_right,_rgba(236,72,153,0.25)_0%,_transparent_65%)]",
  },
  {
    id: "390015604-8",
    url: "https://www.imvu.com/next/chat/room-390015604-8/",
    name: "ISM Social Lounge",
    description: "The go-to gathering space for networking, casual meetups, and connecting with the ISM community.",
    Icon: Sofa,
    accent: "teal",
    accentFrom: "from-teal-950",
    accentVia: "via-cyan-950",
    accentBorder: "border-teal-500/30 hover:border-teal-400/60",
    accentText: "text-teal-400",
    accentBadge: "bg-teal-500/20 border-teal-500/40 text-teal-300",
    accentGlow: "bg-[radial-gradient(ellipse_at_bottom_right,_rgba(20,184,166,0.25)_0%,_transparent_65%)]",
  },
];

const REGIONS = [
  { value: "us-cities",  label: "Earth: US Cities" },
  { value: "europe",     label: "Earth: Europe"   },
  { value: "asia",       label: "Earth: Asia"     },
  { value: "moon",       label: "Moon"            },
  { value: "mars",       label: "Mars"            },
];

export default function Worlds() {
  const [activeRegion, setActiveRegion] = useState<string>("us-cities");
  const [timeOfDay, setTimeOfDay] = useState<"day" | "night">("night");

  const { data: spaces, isLoading } = useListSpaces({ region: activeRegion, timeOfDay });
  const { visibleItems, hasMore, loadMore, total } = useInfiniteItems(spaces, 6);

  return (
    <div className="min-h-screen bg-background text-foreground pb-24">
      {/* Hero */}
      <div className="relative h-64 md:h-96 w-full overflow-hidden">
        <img src={avatarCity} alt="Worlds Hero" className="w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tighter uppercase mb-4">World Explorer</h1>
          <p className="text-xl text-muted-foreground max-w-2xl">Discover infinite environments, claim your space, and connect with citizens across the ISM | Universe.</p>
        </div>
      </div>

      {/* ── Navy Pier Featured 3D Landmark ── */}
      <div className="container mx-auto px-4 mt-8 mb-10">
        <div className="flex items-center gap-2 mb-4">
          <Star className="w-4 h-4 text-primary fill-primary" />
          <span className="text-xs font-mono text-primary tracking-widest uppercase">Featured 3D Landmark</span>
        </div>
        <a
          href="https://www.spatial.io/s/ISMProdCo-Chicago-Lake-Shore-Drivin-66e5aaadbdc313a2baeda8df?share=5476686901017953537"
          target="_blank"
          rel="noopener noreferrer"
        >
          <motion.div
            whileHover={{ scale: 1.01 }}
            transition={{ duration: 0.2 }}
            className="group relative w-full h-52 md:h-64 rounded-xl overflow-hidden border border-primary/30 hover:border-primary/70 transition-colors cursor-pointer"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-slate-900 to-black" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(59,130,246,0.25)_0%,_transparent_70%)]" />
            <div className="absolute inset-0 opacity-10"
              style={{
                backgroundImage: "linear-gradient(rgba(99,179,237,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(99,179,237,0.4) 1px, transparent 1px)",
                backgroundSize: "40px 40px",
              }}
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 border border-primary/30 group-hover:border-primary/60 transition-colors">
                <Box className="w-8 h-8 text-primary" />
              </div>
              <p className="text-xs font-mono text-primary/70 tracking-widest uppercase">Interactive 3D Model</p>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 via-black/50 to-transparent">
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-primary text-xs font-mono mb-1 uppercase tracking-wider">Earth: US Cities · Chicago, IL</p>
                  <h3 className="text-2xl md:text-3xl font-bold uppercase tracking-tight">ISM | Navy Pier</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">3D Landmark Venue · Live Events · Open 24/7</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="bg-black/60 backdrop-blur border border-white/10 px-3 py-1 rounded-full text-xs font-mono flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    1,204 online
                  </div>
                  <span className="text-xs bg-primary/20 border border-primary/40 text-primary px-2 py-0.5 rounded font-mono uppercase tracking-wider">GLB · 3D</span>
                </div>
              </div>
            </div>
          </motion.div>
        </a>
      </div>

      {/* ── Featured Spatial Environments ── */}
      <div className="container mx-auto px-4 mb-10">
        <div className="flex items-center gap-2 mb-4">
          <Theater className="w-4 h-4 text-purple-400" />
          <span className="text-xs font-mono text-purple-400 tracking-widest uppercase">Featured Spatial Environments</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {SPATIAL_CITIES.map((city) => (
            <a
              key={city.name}
              href={city.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <motion.div
                whileHover={{ scale: 1.01 }}
                transition={{ duration: 0.2 }}
                className={`group relative w-full h-52 md:h-64 rounded-xl overflow-hidden border ${city.accentBorder} transition-colors cursor-pointer`}
              >
                {/* Background: photo or gradient fallback */}
                {city.image ? (
                  <>
                    <img
                      src={city.image}
                      alt={city.name}
                      className="absolute inset-0 w-full h-full object-cover opacity-60"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                  </>
                ) : (
                  <>
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-950 via-indigo-950 to-black" />
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(168,85,247,0.3)_0%,_transparent_65%)]" />
                    <div className="absolute inset-0 opacity-[0.07]"
                      style={{
                        backgroundImage: "repeating-linear-gradient(90deg, rgba(168,85,247,0.8) 0px, rgba(168,85,247,0.8) 2px, transparent 2px, transparent 48px)",
                      }}
                    />
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                      <div className={`flex items-center justify-center w-14 h-14 rounded-full bg-purple-500/10 border border-purple-500/30 group-hover:border-purple-400/60 transition-colors`}>
                        <Theater className="w-7 h-7 text-purple-300" />
                      </div>
                      <p className="text-xs font-mono text-purple-400/70 tracking-widest uppercase">Live in Spatial · Enter Now</p>
                    </div>
                  </>
                )}
                {/* Bottom info bar */}
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 via-black/50 to-transparent">
                  <p className={`${city.accentText} text-xs font-mono mb-0.5 uppercase tracking-wider`}>{city.cityLabel}</p>
                  <h3 className="text-lg font-bold uppercase tracking-tight leading-tight">{city.name}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5 mb-2">{city.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="bg-black/60 backdrop-blur border border-white/10 px-2 py-0.5 rounded-full text-xs font-mono flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                      Live on Spatial
                    </div>
                    <span className={`flex items-center gap-1 text-xs ${city.accentBadge} px-2 py-0.5 rounded font-mono uppercase tracking-wider`}>
                      <ExternalLink className="w-3 h-3" /> Enter Space
                    </span>
                  </div>
                </div>
              </motion.div>
            </a>
          ))}
        </div>
      </div>

      {/* ── IMVU Event Rooms ── */}
      <div className="container mx-auto px-4 mb-10">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-4 h-4 text-cyan-400" />
          <span className="text-xs font-mono text-cyan-400 tracking-widest uppercase">IMVU Event Rooms &amp; Venues</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {IMVU_ROOMS.map((room) => (
            <motion.div
              key={room.id}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.18 }}
              className="relative"
            >
              {/* iframe layer — loads IMVU room (requires IMVU login; acts as live preview) */}
              <div className={`relative h-64 rounded-xl overflow-hidden border ${room.accentBorder} transition-colors`}>
                <iframe
                  src={room.url}
                  title={room.name}
                  className="absolute inset-0 w-full h-full"
                  allow="fullscreen"
                  loading="lazy"
                />

                {/* Overlay — always visible; click opens room in new tab */}
                <a
                  href={room.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute inset-0 flex flex-col justify-between p-5 group"
                  style={{ background: "linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.55) 50%, rgba(0,0,0,0.30) 100%)" }}
                >
                  {/* Top row */}
                  <div className="flex justify-between items-start">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full bg-black/50 border ${room.accentBorder} backdrop-blur-sm`}>
                      <room.Icon className={`w-5 h-5 ${room.accentText}`} />
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      <div className="bg-black/60 backdrop-blur border border-white/10 px-2.5 py-1 rounded-full text-xs font-mono flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                        Live on IMVU
                      </div>
                      <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded font-mono uppercase tracking-wider border ${room.accentBadge}`}>
                        <ExternalLink className="w-3 h-3" /> IMVU
                      </span>
                    </div>
                  </div>

                  {/* Bottom info */}
                  <div>
                    <h3 className={`text-lg font-bold uppercase tracking-tight mb-1 ${room.accentText} group-hover:text-white transition-colors`}>
                      {room.name}
                    </h3>
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{room.description}</p>
                    <div className={`flex items-center justify-center gap-2 w-full py-2 rounded-lg border ${room.accentBorder} ${room.accentText} text-xs font-mono uppercase tracking-widest group-hover:bg-white/5 transition-colors`}>
                      <ExternalLink className="w-3.5 h-3.5" />
                      Enter Room
                    </div>
                  </div>
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="container mx-auto px-4">
        {/* ── Filters row — horizontally scrollable ── */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-4">
          {/* Region tabs — scroll on narrow screens */}
          <div className="w-full sm:w-auto overflow-x-auto pb-1 no-scrollbar">
            <Tabs defaultValue="us-cities" onValueChange={setActiveRegion} className="w-max">
              <TabsList className="bg-card border border-border flex-nowrap">
                {REGIONS.map(r => (
                  <TabsTrigger key={r.value} value={r.value} className="whitespace-nowrap"
                    data-testid={`tab-${r.value}`}>
                    {r.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {/* Day / Night toggle */}
          <div className="flex items-center space-x-3 bg-card px-4 py-2 rounded-full border border-border flex-shrink-0">
            <Label htmlFor="time-toggle" className="text-sm font-medium">Day</Label>
            <Switch
              id="time-toggle"
              checked={timeOfDay === "night"}
              onCheckedChange={(c) => setTimeOfDay(c ? "night" : "day")}
              data-testid="toggle-time-of-day"
            />
            <Label htmlFor="time-toggle" className="text-sm font-medium">Night</Label>
          </div>
        </div>

        {/* ── Grid ── */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="h-80 bg-card/50 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {visibleItems.map((space, idx) => (
                <motion.div
                  key={space.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: (idx % 6) * 0.07 }}
                >
                  <Card className="overflow-hidden border-border bg-card/40 hover:border-primary/50 transition-colors group">
                    <div className="relative h-48 w-full">
                      <img src={space.imageUrl} alt={space.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-md px-3 py-1 rounded-full text-xs font-mono border border-white/10 flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                        {space.onlineCount} Online
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-2xl font-bold uppercase tracking-tight">{space.name}</h3>
                          <p className="text-sm text-primary">{space.regionLabel || space.region}</p>
                        </div>
                        <div className="text-xs text-muted-foreground uppercase border border-border px-2 py-1 rounded">
                          {space.category}
                        </div>
                      </div>
                      <p className="text-muted-foreground text-sm mb-6 line-clamp-2">{space.description}</p>
                      <Link href={`/spaces/${space.id}`}>
                        <Button className="w-full bg-primary hover:bg-primary/80 text-primary-foreground font-bold tracking-widest uppercase" data-testid={`btn-enter-${space.id}`}>
                          Enter Space
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}

              {spaces?.length === 0 && (
                <div className="col-span-full text-center py-24 text-muted-foreground">
                  <p className="text-xl">No spaces found for this region and time.</p>
                </div>
              )}
            </div>

            <div className="mt-10 flex flex-col items-center gap-3">
              {hasMore && (
                <button
                  onClick={loadMore}
                  className="border border-white/20 hover:border-primary hover:text-primary font-mono uppercase tracking-widest px-8 py-2 rounded text-sm transition-colors"
                >
                  Load More Spaces
                </button>
              )}
              {!hasMore && total > 0 && (
                <p className="text-xs text-muted-foreground font-mono">
                  Showing all {total} space{total !== 1 ? "s" : ""}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
